import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Container,
  Dimmer,
  Dropdown,
  Form,
  Icon,
  Label,
  Loader,
  Message,
  Modal,
  Segment,
  Table,
} from "semantic-ui-react";
import { useAuth } from "../context/AuthContext";
import { stockyApi } from "../misc/StockyApi";
import {
  pageBodyStyle,
  pageFrameStyle,
  pageShellStyle,
  surfaceCardStyle,
} from "../misc/pageStyles";
import ReportCsvExportModal from "../reports/ReportCsvExportModal";
import {
  movementActorTypeOptions,
  movementChannelOptions,
  movementExportFields,
  movementInitialFilters,
  movementSourceTypeOptions,
  movementStatusOptions,
  movementTypeOptions,
  normalizeMovementReportFilters,
  REPORT_FILTER_ALL_VALUE,
  resolveReportSelectValue,
  validateReportFilters,
} from "../reports/reportFilters";
import {
  arrangeMovementRows,
  formatChannelName,
  formatMovementDateTime,
  formatStockChange,
  getMovementContext,
  getFanOutDetailSummary,
  getMovementLabel,
  getMovementResult,
  getMovementSubtitle,
  getMovementVariant,
} from "./movementPresentation";
import "./stockMovementsDashboard.css";

const MOVEMENT_PAGE_SIZE = 30;

const normalizeMovementRow = (row) => ({
  id: row.id,
  sku: row.sku,
  item_id: row.item_id ?? row.itemId ?? null,
  item_title: row.item_title ?? row.itemTitle ?? null,
  kit_item_id: row.kit_item_id ?? row.kitItemId ?? null,
  kit_listing_id: row.kit_listing_id ?? row.kitListingId ?? null,
  kit_title: row.kit_title ?? row.kitTitle ?? null,
  kit_sku: row.kit_sku ?? row.kitSku ?? null,
  stock_composition: row.stock_composition ?? row.stockComposition ?? null,
  metadata_reason: row.metadata_reason ?? row.metadataReason ?? null,
  fan_out_updated_count:
    row.fan_out_updated_count ?? row.fanOutUpdatedCount ?? 0,
  fan_out_failed_count: row.fan_out_failed_count ?? row.fanOutFailedCount ?? 0,
  fan_out_skipped_count:
    row.fan_out_skipped_count ?? row.fanOutSkippedCount ?? 0,
  fan_out_internal_only_count:
    row.fan_out_internal_only_count ?? row.fanOutInternalOnlyCount ?? 0,
  fan_out_marketplace_order_applied_count:
    row.fan_out_marketplace_order_applied_count ??
    row.fanOutMarketplaceOrderAppliedCount ??
    0,
  parent_sale_movement_id:
    row.parent_sale_movement_id ?? row.parentSaleMovementId ?? null,
  quantity_delta: row.quantity_delta ?? row.quantityDelta ?? 0,
  stock_before: row.stock_before ?? row.stockBefore ?? 0,
  stock_after: row.stock_after ?? row.stockAfter ?? 0,
  movement_type: row.movement_type ?? row.movementType ?? "",
  source_type: row.source_type ?? row.sourceType ?? "",
  source_channel: row.source_channel ?? row.sourceChannel ?? "",
  actor_type: row.actor_type ?? row.actorType ?? "",
  actor_id: row.actor_id ?? row.actorId ?? "",
  actor_label: row.actor_label ?? row.actorLabel ?? "",
  external_order_id: row.external_order_id ?? row.externalOrderId ?? "",
  created_at: row.created_at ?? row.createdAt ?? null,
});

const SelectFilter = ({ id, label, options, value, onChange, describedBy }) => (
  <Form.Field>
    <label id={`${id}-label`}>{label}</label>
    <Dropdown
      id={id}
      aria-labelledby={`${id}-label`}
      aria-describedby={describedBy}
      selection
      fluid
      options={options}
      value={resolveReportSelectValue(value, options)}
      onChange={(_, data) => onChange(String(data.value || ""))}
    />
  </Form.Field>
);

function StockMovementsDashboard() {
  const Auth = useAuth();
  const user = Auth.user;
  const accessToken = user?.accessToken || "";
  const authUser = useMemo(
    () => (accessToken ? { accessToken } : null),
    [accessToken],
  );

  const [pendingFilters, setPendingFilters] = useState(movementInitialFilters);
  const [filters, setFilters] = useState(movementInitialFilters);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sellerNicknameById, setSellerNicknameById] = useState({});
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [metadataModalOpen, setMetadataModalOpen] = useState(false);
  const [metadataRow, setMetadataRow] = useState(null);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [metadataError, setMetadataError] = useState("");
  const [exportModalOpen, setExportModalOpen] = useState(false);

  const totalPages = Math.max(1, Math.ceil(total / MOVEMENT_PAGE_SIZE));
  const queryParams = useMemo(
    () => ({ ...filters, page, pageSize: MOVEMENT_PAGE_SIZE }),
    [filters, page],
  );
  const arrangedRows = useMemo(() => arrangeMovementRows(rows), [rows]);

  useEffect(() => {
    if (!authUser) return undefined;
    let alive = true;
    (async () => {
      try {
        const sellersRes = await stockyApi.getSellers(authUser);
        if (!alive) return;
        const sellersMap = {};
        (sellersRes.data || []).forEach((seller) => {
          if (seller?.id && seller?.nickname)
            sellersMap[String(seller.id)] = seller.nickname;
        });
        setSellerNicknameById(sellersMap);
      } catch (lookupError) {
        console.error("Error loading movement lookup data:", lookupError);
      }
    })();
    return () => {
      alive = false;
    };
  }, [authUser]);

  useEffect(() => {
    if (!authUser) return undefined;
    let alive = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const listRes = await stockyApi.getStockMovements(
          authUser,
          queryParams,
        );
        if (!alive) return;
        setRows((listRes.data?.data || []).map(normalizeMovementRow));
        setTotal(Number(listRes.data?.total || 0));
      } catch (requestError) {
        if (!alive) return;
        console.error("Error loading stock movements:", requestError);
        setError("No se pudieron cargar los movimientos de stock.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [authUser, queryParams]);

  const resolveActorLabel = (row) => {
    const rawLabel = row.actor_label || "";
    const rawId = row.actor_id || "";
    if (rawLabel && rawLabel !== rawId) return rawLabel;
    if (row.actor_type === "MARKETPLACE_ACCOUNT" && rawId) {
      return sellerNicknameById[String(rawId)] || rawId;
    }
    return rawLabel || rawId || "";
  };

  const openMetadata = async (row) => {
    setMetadataError("");
    setMetadataLoading(true);
    setMetadataRow(row);
    setMetadataModalOpen(true);
    try {
      const response = await stockyApi.getStockMovementMetadata(
        authUser,
        row.id,
      );
      setMetadataRow((previous) =>
        previous && previous.id === row.id
          ? { ...previous, metadata: response?.data?.metadata || {} }
          : previous,
      );
    } catch (requestError) {
      console.error("Error loading movement metadata:", requestError);
      setMetadataError("No se pudo cargar el detalle técnico del movimiento.");
    } finally {
      setMetadataLoading(false);
    }
  };

  const applyFilters = () => {
    setFilters(normalizeMovementReportFilters(pendingFilters));
    setPage(1);
  };

  const clearFilters = () => {
    setPendingFilters(movementInitialFilters);
    setFilters(movementInitialFilters);
    setPage(1);
  };

  const handleMovementTypeChange = (value) => {
    setPendingFilters((previous) => ({
      ...previous,
      movementType: value,
      movementStatus:
        value === "SYNC_ADJUSTMENT" || value === REPORT_FILTER_ALL_VALUE
          ? previous.movementStatus
          : "",
    }));
  };

  const handleMovementStatusChange = (value) => {
    setPendingFilters((previous) => ({
      ...previous,
      movementStatus: value,
      movementType:
        value && value !== REPORT_FILTER_ALL_VALUE
          ? "SYNC_ADJUSTMENT"
          : previous.movementType,
    }));
  };

  const renderDetailButton = (movement, hasError = false) => (
    <Button
      basic
      color="violet"
      size="mini"
      className="movement-detail-button"
      icon="eye"
      content={hasError ? "Ver error" : "Ver"}
      aria-label={`Ver detalle del movimiento ${movement.sku || movement.id}`}
      onClick={() => openMetadata(movement)}
    />
  );

  const renderMovementRow = ({ movement, isChild, childKind }) => {
    const movementWithActor = {
      ...movement,
      actor_display_label: resolveActorLabel(movement),
    };
    const stock = formatStockChange(
      movement.stock_before,
      movement.stock_after,
      movement.quantity_delta,
    );
    const context = getMovementContext(movementWithActor);
    const result = getMovementResult(movement);
    const variant = getMovementVariant(movement.movement_type);
    if (isChild) {
      const isKitComponent = childKind === "kit-component";
      const childSummary = isKitComponent
        ? {
            title: "Sincronización de stock",
            subtitle: `Componente · SKU: ${movement.sku || "Sin SKU"}${
              movement.item_title ? ` · ${movement.item_title}` : ""
            }`,
          }
        : {
            title: "Sincronización de stock",
            subtitle: context.primary,
          };
      return (
        <Table.Row key={movement.id} className="movements-table__row--child">
          <Table.Cell
            className="movements-table__linked-rail"
            aria-hidden="true"
          />
          <Table.Cell
            colSpan={isKitComponent ? 2 : 4}
            data-label="Movimiento"
            className="movements-table__linked-content"
          >
            <div className="movement-linked-update__summary">
              <Icon name="refresh" aria-hidden="true" />
              <div>
                <strong>{childSummary.title}</strong>
                <span title={childSummary.subtitle}>{childSummary.subtitle}</span>
              </div>
            </div>
          </Table.Cell>
          {isKitComponent ? (
            <Table.Cell data-label="Stock" className="movements-table__linked-stock">
              <div className="movement-stock">
                <span className="movement-stock__transition">
                  <strong>{stock.before}</strong>
                  <span className="movement-stock__arrow" aria-hidden="true">→</span>
                  <strong>{stock.after}</strong>
                </span>
                {stock.deltaLabel ? (
                  <span className={`movement-delta movement-delta--${stock.deltaTone}`}>
                    {stock.deltaLabel}
                  </span>
                ) : null}
              </div>
            </Table.Cell>
          ) : null}
          {isKitComponent ? (
            <Table.Cell data-label="Contexto" className="movements-table__linked-context">
              <div className="movement-context">
                <strong>{context.primary}</strong>
                {context.secondary ? <span title={context.secondary}>{context.secondary}</span> : null}
              </div>
            </Table.Cell>
          ) : null}
          <Table.Cell
            data-label="Resultado"
            className="movements-table__linked-result"
          >
            <div className={`movement-result movement-result--${result.tone}`}>
              <Icon name={result.icon} aria-hidden="true" />
              <div>
                <strong>{result.primary}</strong>
                {result.secondary ? <span>{result.secondary}</span> : null}
              </div>
            </div>
          </Table.Cell>
          <Table.Cell
            data-label="Detalle"
            textAlign="center"
            className="movements-table__linked-action"
          >
            {renderDetailButton(movement, result.tone === "error")}
          </Table.Cell>
        </Table.Row>
      );
    }

    const movementSubtitle = getMovementSubtitle(movement);
    const isKitSaleSummary = movement.presentation_type === "KIT_SALE";

    return (
      <Table.Row key={movement.id} className="movements-table__row">
        <Table.Cell data-label="Fecha y hora" className="movements-table__date">
          <span>{formatMovementDateTime(movement.created_at)}</span>
        </Table.Cell>
        <Table.Cell data-label="Producto">
          <div className="movement-product">
            <strong>{movement.sku || "SKU no informado"}</strong>
            {movement.item_title ? (
              <span title={movement.item_title}>{movement.item_title}</span>
            ) : (
              <span>Producto sin nombre disponible</span>
            )}
          </div>
        </Table.Cell>
        <Table.Cell data-label="Movimiento">
          <div className="movement-type">
            <Label className={`movement-badge movement-badge--${variant}`}>
              {getMovementLabel(movement.movement_type)}
            </Label>
            {movementSubtitle ? <span>{movementSubtitle}</span> : null}
          </div>
        </Table.Cell>
        <Table.Cell data-label="Stock">
          {isKitSaleSummary ? (
            <span className="movement-stock__derived">Derivado de sus componentes</span>
          ) : (
            <div className="movement-stock">
              <span
                className="movement-stock__transition"
                aria-label={stock.transition}
              >
                <strong aria-hidden="true">{stock.before}</strong>
                <span className="movement-stock__arrow" aria-hidden="true">
                  →
                </span>
                <strong aria-hidden="true">{stock.after}</strong>
              </span>
              {stock.deltaLabel ? (
                <span
                  className={`movement-delta movement-delta--${stock.deltaTone}`}
                >
                  {stock.deltaLabel}
                </span>
              ) : null}
            </div>
          )}
        </Table.Cell>
        <Table.Cell data-label="Contexto">
          <div className="movement-context">
            <strong>{context.primary}</strong>
            {context.secondary ? (
              <span title={context.secondary}>{context.secondary}</span>
            ) : null}
          </div>
        </Table.Cell>
        <Table.Cell data-label="Resultado">
          <div className={`movement-result movement-result--${result.tone}`}>
            <Icon name={result.icon} aria-hidden="true" />
            <div>
              <strong>{result.primary}</strong>
              {result.secondary ? <span>{result.secondary}</span> : null}
            </div>
          </div>
        </Table.Cell>
        <Table.Cell data-label="Detalle" textAlign="center">
          {isKitSaleSummary
            ? null
            : renderDetailButton(movement, result.tone === "error")}
        </Table.Cell>
      </Table.Row>
    );
  };

  const firstVisible = total === 0 ? 0 : (page - 1) * MOVEMENT_PAGE_SIZE + 1;
  const lastVisible = Math.min(page * MOVEMENT_PAGE_SIZE, total);
  const detailFanOutSummary = getFanOutDetailSummary(metadataRow || {});

  return (
    <Container fluid style={pageShellStyle}>
      <Segment style={{ ...pageFrameStyle, background: "#F8FAFC" }}>
        <div style={pageBodyStyle} className="movements-page">
          {error ? (
            <Message negative role="alert">
              <Message.Header>Error</Message.Header>
              <p>{error}</p>
            </Message>
          ) : null}

          <section aria-labelledby="movement-filters-title">
            <div className="movements-section-heading">
              <h2 id="movement-filters-title">Filtros</h2>
              <p>Busca y filtra los movimientos por fecha, SKU, canal y más.</p>
            </div>
            <Segment
              style={surfaceCardStyle}
              className="movements-card movements-filter-card"
            >
              <Form
                onSubmit={(event) => {
                  event.preventDefault();
                  applyFilters();
                }}
              >
                <div className="movements-filter-grid">
                  <Form.Input
                    id="movement-filter-from"
                    label="Desde"
                    type="datetime-local"
                    value={pendingFilters.from}
                    onChange={(event) =>
                      setPendingFilters((previous) => ({
                        ...previous,
                        from: event.target.value,
                      }))
                    }
                  />
                  <Form.Input
                    id="movement-filter-to"
                    label="Hasta"
                    type="datetime-local"
                    value={pendingFilters.to}
                    onChange={(event) =>
                      setPendingFilters((previous) => ({
                        ...previous,
                        to: event.target.value,
                      }))
                    }
                  />
                  <Form.Input
                    id="movement-filter-sku"
                    label="SKU"
                    placeholder="Buscar por SKU (mínimo 3 caracteres)..."
                    value={pendingFilters.sku}
                    onChange={(event) =>
                      setPendingFilters((previous) => ({
                        ...previous,
                        sku: event.target.value,
                      }))
                    }
                  />
                  <SelectFilter
                    id="movement-filter-source"
                    label="Origen"
                    options={movementSourceTypeOptions}
                    value={pendingFilters.sourceType}
                    onChange={(value) =>
                      setPendingFilters((previous) => ({
                        ...previous,
                        sourceType: value,
                      }))
                    }
                  />
                  <SelectFilter
                    id="movement-filter-channel"
                    label="Canal"
                    options={movementChannelOptions}
                    value={pendingFilters.sourceChannel}
                    onChange={(value) =>
                      setPendingFilters((previous) => ({
                        ...previous,
                        sourceChannel: value,
                      }))
                    }
                  />
                  <SelectFilter
                    id="movement-filter-actor"
                    label="Actor"
                    options={movementActorTypeOptions}
                    value={pendingFilters.actorType}
                    onChange={(value) =>
                      setPendingFilters((previous) => ({
                        ...previous,
                        actorType: value,
                      }))
                    }
                  />
                  <SelectFilter
                    id="movement-filter-type"
                    label="Tipo de movimiento"
                    options={movementTypeOptions}
                    value={pendingFilters.movementType}
                    onChange={handleMovementTypeChange}
                  />
                  <SelectFilter
                    id="movement-filter-status"
                    label="Estado"
                    options={movementStatusOptions}
                    value={pendingFilters.movementStatus}
                    onChange={handleMovementStatusChange}
                  />
                  <div className="movements-filter-actions">
                    <Button color="purple" type="submit">
                      Aplicar filtros
                    </Button>
                    <Button basic type="button" onClick={clearFilters}>
                      Limpiar
                    </Button>
                  </div>
                </div>
              </Form>
            </Segment>
          </section>

          <section aria-labelledby="movement-list-title">
            <div className="movements-list-header">
              <div className="movements-section-heading">
                <h2 id="movement-list-title">Listado</h2>
                <p>
                  Historial de cambios de stock y sincronizaciones entre
                  canales.
                </p>
              </div>
              <Button
                color="purple"
                icon="download"
                content="Exportar CSV"
                onClick={() => setExportModalOpen(true)}
              />
            </div>
            <Segment
              style={surfaceCardStyle}
              className="movements-card movements-list-card"
            >
              <div className="responsive-table-container">
                <Table className="movements-table" basic="very" unstackable>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>Fecha y hora</Table.HeaderCell>
                      <Table.HeaderCell>Producto</Table.HeaderCell>
                      <Table.HeaderCell>Movimiento</Table.HeaderCell>
                      <Table.HeaderCell>Stock</Table.HeaderCell>
                      <Table.HeaderCell>Contexto</Table.HeaderCell>
                      <Table.HeaderCell>Resultado</Table.HeaderCell>
                      <Table.HeaderCell textAlign="center">
                        Detalle
                      </Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {loading ? (
                      <Table.Row>
                        <Table.Cell
                          colSpan={7}
                          textAlign="center"
                          className="movements-table__state"
                        >
                          <Dimmer active inverted>
                            <Loader>Cargando movimientos...</Loader>
                          </Dimmer>
                        </Table.Cell>
                      </Table.Row>
                    ) : arrangedRows.length === 0 ? (
                      <Table.Row>
                        <Table.Cell
                          colSpan={7}
                          textAlign="center"
                          className="movements-table__state"
                        >
                          <Icon name="inbox" size="large" aria-hidden="true" />
                          <strong>
                            No hay movimientos para los filtros seleccionados.
                          </strong>
                          <span>
                            Probá con otro período o limpiá los filtros.
                          </span>
                        </Table.Cell>
                      </Table.Row>
                    ) : (
                      arrangedRows.map(renderMovementRow)
                    )}
                  </Table.Body>
                </Table>
              </div>
              <div
                className="movements-pagination"
                aria-label="Paginación de movimientos"
              >
                <Button
                  basic
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((previous) => previous - 1)}
                >
                  Anterior
                </Button>
                <span>
                  Mostrando {firstVisible}–{lastVisible} de {total} movimientos
                </span>
                <Button
                  basic
                  disabled={page >= totalPages || loading}
                  onClick={() => setPage((previous) => previous + 1)}
                >
                  Siguiente
                </Button>
              </div>
            </Segment>
          </section>
        </div>
      </Segment>

      <ReportCsvExportModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        title="Exportar movimientos de stock"
        description="Se exportará una fila por movimiento con los campos técnicos útiles para auditoría."
        initialFilters={filters}
        fields={movementExportFields}
        normalizeFilters={normalizeMovementReportFilters}
        validateFilters={(values) =>
          validateReportFilters(values, { minimumSkuLength: 3 })
        }
        requestExport={(values, signal) =>
          stockyApi.exportStockMovementsCsv(authUser, values, signal)
        }
        fallbackFilename="stocky-movimientos.csv"
      />

      <Modal
        open={metadataModalOpen}
        onClose={() => setMetadataModalOpen(false)}
        closeIcon
      >
        <Modal.Header>Detalle del movimiento</Modal.Header>
        <Modal.Content>
          {metadataRow ? (
            <div className="movement-detail-summary">
              <div>
                <span>Producto</span>
                <strong>{metadataRow.sku || "No informado"}</strong>
              </div>
              <div>
                <span>Tipo</span>
                <strong>{getMovementLabel(metadataRow.movement_type)}</strong>
              </div>
              <div>
                <span>Canal</span>
                <strong>{formatChannelName(metadataRow.source_channel)}</strong>
              </div>
              <div>
                <span>Fecha completa</span>
                <strong>
                  {metadataRow.created_at
                    ? new Date(metadataRow.created_at).toLocaleString("es-AR")
                    : "No informada"}
                </strong>
              </div>
              {metadataRow.external_order_id ? (
                <div>
                  <span>Orden externa</span>
                  <strong>#{metadataRow.external_order_id}</strong>
                </div>
              ) : null}
              <div>
                <span>Actor</span>
                <strong>
                  {resolveActorLabel(metadataRow) || "No informado"}
                </strong>
              </div>
            </div>
          ) : null}
          {metadataError ? <Message negative content={metadataError} /> : null}
          {detailFanOutSummary ? (
            <Message info>
              <Message.Header>Resultado de actualización</Message.Header>
              <p>{detailFanOutSummary}</p>
            </Message>
          ) : null}
          {metadataLoading ? (
            <Segment basic>
              <Loader active inline="centered" size="small">
                Cargando detalle...
              </Loader>
            </Segment>
          ) : (
            <>
              <h3>Información técnica</h3>
              <pre className="movement-technical-detail">
                {JSON.stringify(metadataRow?.metadata || {}, null, 2)}
              </pre>
            </>
          )}
        </Modal.Content>
      </Modal>
    </Container>
  );
}

export default StockMovementsDashboard;
